import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Star, ArrowRight, Heart, Shield, Clock, Stethoscope } from 'lucide-react';
import { nurseInfo, services, medicalImages } from '../data/mock';
import { useTestimonials } from '../hooks/useApi';
import { StatsSection, MedicalFeaturesSection, CTASection } from '../components/BeautifulSections';

const Home = () => {
  const featuredServices = services.slice(0, 2);
  
  // Use API hook for testimonials
  const { testimonials, loading: testimonialsLoading } = useTestimonials();
  const featuredTestimonials = testimonials.slice(0, 2);

  return (
    <div>
      {/* Enhanced Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Stethoscope size={20} className="text-blue-500" />
            <span>Soins infirmiers à domicile</span>
          </div>
          
          <h1 className="heading-hero mb-8">
            PodioSoins+
            <br />
            Votre santé, nos soins
          </h1>
          
          <p className="body-large text-gray-800 mb-12 max-w-2xl mx-auto leading-relaxed">
            Spécialiste en soins de plaies et de pieds à Longueuil. 
            Des soins professionnels personnalisés dans le confort de votre foyer.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Link to="/contact" className="btn-primary hover-scale">
              <Calendar size={20} className="mr-3" />
              <span className="button-text">Prendre rendez-vous</span>
            </Link>
            <a href="tel:819-469-4419" className="btn-secondary hover-scale">
              <Phone size={20} className="mr-3" />
              <span className="button-text">Appeler: 819-469-4419</span>
            </a>
          </div>

          {/* Hero Medical Services Preview */}
          <div className="relative max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="service-card accent-blue hover-lift">
                <img 
                  src={medicalImages.woundCare.professional} 
                  alt="Soins de plaies professionnels"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="heading-3 mb-2">Soins de plaies</h3>
                <p className="body-small text-gray-600">Traitement professionnel et suivi personnalisé</p>
              </div>
              
              <div className="service-card accent-green hover-lift">
                <img 
                  src={medicalImages.footCare.examination} 
                  alt="Soins de pieds spécialisés"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="heading-3 mb-2">Soins de pieds</h3>
                <p className="body-small text-gray-600">Expertise podologique à domicile</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <StatsSection />

      {/* Enhanced Nurse Introduction */}
      <section className="section-spacing bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 mb-4">
                  <Heart size={24} className="text-red-500" />
                  <span className="body-small font-semibold text-gray-600 uppercase tracking-wide">
                    Votre infirmière
                  </span>
                </div>
                <h2 className="heading-1">
                  {nurseInfo.name}
                </h2>
                <p className="body-large text-gray-800 leading-relaxed">
                  {nurseInfo.title} avec {nurseInfo.experience} dans le domaine médical. 
                  Spécialisée en {nurseInfo.specializations.join(' et ')}, 
                  je vous offre des soins de qualité dans le confort de votre foyer.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="service-card accent-blue text-center">
                  <Shield size={32} className="text-blue-600 mx-auto mb-3" />
                  <p className="body-medium font-semibold">Certifiée OIIQ</p>
                  <p className="caption text-gray-600">Ordre professionnel</p>
                </div>
                <div className="service-card accent-green text-center">
                  <Clock size={32} className="text-green-600 mx-auto mb-3" />
                  <p className="body-medium font-semibold">{nurseInfo.experience}</p>
                  <p className="caption text-gray-600">D'expertise</p>
                </div>
              </div>
              
              <Link to="/about" className="btn-secondary inline-flex items-center hover-scale">
                <span className="button-text">En savoir plus</span>
                <ArrowRight size={20} className="ml-3" />
              </Link>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src={nurseInfo.image} 
                  alt="Salwa Chraibi, Infirmière spécialisée"
                  className="w-full h-[500px] object-cover hover-lift"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>
              
              {/* Professional credentials card */}
              <div className="absolute -bottom-8 -right-8 service-card accent-purple">
                <div className="flex items-center space-x-3 mb-3">
                  <Shield size={24} className="text-blue-600" />
                  <span className="heading-3">OIIQ</span>
                </div>
                <p className="body-small text-gray-700 font-medium">Membre certifiée</p>
                <p className="caption text-gray-600">Ordre des infirmières du Québec</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Features Section */}
      <MedicalFeaturesSection />

      {/* Enhanced Featured Services */}
      <section className="section-spacing bg-pattern-waves">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <Stethoscope size={24} className="text-blue-500" />
              <span className="body-small font-semibold text-gray-600 uppercase tracking-wide">
                Nos services
              </span>
            </div>
            <h2 className="heading-1 mb-6">Soins spécialisés à domicile</h2>
            <p className="body-large text-gray-800 max-w-3xl mx-auto">
              Des services professionnels adaptés à vos besoins, 
              dans le confort et la sécurité de votre domicile.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {featuredServices.map((service) => (
              <div 
                key={service.id} 
                className={`service-card ${service.accent} hover-lift`}
              >
                <div className="mb-8">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-64 object-cover rounded-xl service-image"
                  />
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="heading-2 mb-4">{service.title}</h3>
                    <p className="body-medium text-gray-700 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {service.services.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex-shrink-0"></div>
                        <span className="body-small text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <p className="body-medium font-semibold gradient-text">
                      {service.pricing}
                    </p>
                    <Link 
                      to="/services" 
                      className="btn-secondary hover-scale"
                    >
                      <span className="button-text">En savoir plus</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary hover-scale">
              <span className="button-text">Voir tous nos services</span>
              <ArrowRight size={20} className="ml-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section supprimée : Notre expertise en images */}

      {/* Simple Testimonials Section */}
      {testimonialsLoading ? (
        <section className="section-spacing bg-white">
          <div className="container">
            <div className="text-center py-16">
              <div className="loading-pulse">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mx-auto mb-4"></div>
                <p className="body-medium text-gray-600">Chargement des témoignages...</p>
              </div>
            </div>
          </div>
        </section>
      ) : testimonials.length > 0 ? (
        <section className="section-spacing bg-white">
          <div className="container">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-6">
                <Star size={24} className="text-yellow-500" />
                <span className="body-small font-semibold text-gray-600 uppercase tracking-wide">
                  Témoignages
                </span>
              </div>
              <h2 className="heading-1 mb-6">Avis de nos patients</h2>
              <p className="body-large text-gray-800 max-w-2xl mx-auto">
                Découvrez l'expérience de patients qui ont fait confiance à nos services.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {featuredTestimonials.map((testimonial) => (
                <div key={testimonial.id} className="service-card accent-cream hover-lift">
                  <div className="flex items-center space-x-2 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-400" fill="currentColor" />
                    ))}
                  </div>
                  
                  <blockquote className="mb-8">
                    <p className="body-large text-gray-700 italic leading-relaxed">
                      "{testimonial.text}"
                    </p>
                  </blockquote>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="body-medium font-semibold">{testimonial.name}</p>
                      <p className="body-small text-gray-600">
                        {testimonial.age} • {testimonial.location}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="body-small font-medium gradient-text">
                        {testimonial.service}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {testimonials.length > 2 && (
              <div className="text-center mt-12">
                <Link to="/testimonials" className="btn-secondary hover-scale">
                  <span className="button-text">Lire plus de témoignages</span>
                  <ArrowRight size={20} className="ml-3" />
                </Link>
              </div>
            )}
          </div>
        </section>
      ) : null}

      {/* Simple CTA Section */}
      <CTASection 
        title="Prêt à recevoir des soins professionnels ?"
        description="Contactez Salwa dès aujourd'hui pour planifier une consultation personnalisée et découvrir comment nos soins spécialisés peuvent améliorer votre bien-être."
        primaryButton={
          <Link to="/contact" className="btn-primary hover-scale">
            <Calendar size={20} className="mr-3" />
            <span className="button-text">Prendre rendez-vous</span>
          </Link>
        }
        secondaryButton={
          <a href="tel:819-469-4419" className="btn-secondary hover-scale">
            <Phone size={20} className="mr-3" />
            <span className="button-text">Appeler maintenant</span>
          </a>
        }
      />
    </div>
  );
};

export default Home;